import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row } from 'reactstrap';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { InView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';

import { fetchAllGames } from 'Redux/ducks/algolia-calls';
import Backtotopbutton from 'Components/Utility/Mobile/Backtotopbutton';
import facetMessages from 'Components/FacetSearch/LanguagesStrings';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import HitCard from 'Components/Cards/HitCard';
import Meta from 'Components/Utility/SEO/Meta';
import NotFound from 'Components/NotFound/NotFound';
import Paginate from 'Components/Utility/Paginate';
import PriceContainer from 'Components/Utility/Price/PriceContainer';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import TakeOver from 'Components/Banner/TakeOver';

const messages = defineMessages({
  title: {
    id: 'all-games.title',
    defaultMessage: 'Browse All PC Games',
  },
  seoTitle: {
    id: 'all-games.seoTitle',
    defaultMessage: 'PC Games',
  },
  desc: {
    id: 'all-games.desc',
    defaultMessage: 'See our full list of PC games from your favourite genres and franchises with the biggest savings you\'ll find online',
  },
  page: {
    id: 'all-games.page',
    defaultMessage: 'Page',
  },
});

const AllGames = ({
  location: { search },
  intl: { formatMessage },
}) => {
  const [hitsObject, setHits] = useState({ hits: null, nbHits: null, hitsPerPage: 72 });
  const [fetchError, setFetchError] = useState(null);
  const page = Number(getUrlParameter('page', search) || 1);


  useEffect(() => {
    const fetchHits = async () => {
      const hits = await fetchAllGames(72, page - 1);
      setHits(hits);
    };

    fetchHits().catch(setFetchError);
  }, [page]);

  if (fetchError) {
    throw fetchError;
  }

  const { hits, nbHits, hitsPerPage } = hitsObject;

  const seoTitle = `${formatMessage(messages.seoTitle)}${page > 1 ? ` | ${formatMessage(messages.page)} ${page}` : ''} | Fanatical`;
  const h1Title = `${formatMessage(messages.title)}${page > 1 ? `- ${formatMessage(messages.page)} ${page}` : ''}`;

  const desc = formatMessage(messages.desc);

  return (
    <React.Fragment>
      <Meta
        title={seoTitle}
        description={desc}
        url="all-games"
        hits={hits}
        nbHits={nbHits}
        hitsPerPage={hitsPerPage}
        pageType="CollectionPage"
      />
      {hits && !hits.length &&
        <NotFound />
      }
      {((hits && hits.length) || hits === null) &&
        <React.Fragment>
          <TakeOver appearsOn={'all_games'} />
          <div className="container">
            <InView>
              {({ inView, ref }) => (
                <div ref={ref}>
                  <h1 className="pt-5 mb-3">{h1Title}</h1>
                  {!inView &&
                    <div className="d-block d-sm-none">
                      <Backtotopbutton />
                    </div>
                  }
                </div>
              )}
            </InView>
            <p className="mb-4">{desc}</p>
            <p className="mb-4"><FormattedMessage id="all-games.browse" defaultMessage="Browse By" />:
              <ul className="inline-list">
                <li><Link to="/categories">{formatMessage(facetMessages.category)}</Link></li>
                <li><Link to="/franchises">{formatMessage(facetMessages.franchise)}</Link></li>
                <li><Link to="/publishers">{formatMessage(facetMessages.publisher)}</Link></li>
              </ul>
            </p>

            <React.Fragment>
              {hits === null &&
                <div className="row">
                  <SkeletonHitCard
                    numberOfCards={72}
                    lgSize={3}
                  />
                </div>
              }

              {!isEmpty(hits) &&
                <ul className="HitCardsRow row">
                  {hits.map((hit, i) =>
                    <HitCard
                      key={hit.objectID}
                      hit={hit}
                      lgSize={3}
                      lazy={i >= 12}
                      hitCardContainerTag="li"
                      hitCardNameTag="h2"
                    >
                      <PriceContainer
                        discountPercent={hit.discount_percent}
                        price={hit.price}
                        tiered={hit.tiered}
                        slug={hit.slug}
                        hideDiscount={hit.hide_discount}
                        payWhatYouWant={hit.pay_what_you_want}
                      />
                    </HitCard>,
                  )}
                </ul>
              }

            </React.Fragment>

            <Row>
              <Paginate hits={hitsObject} />
            </Row>
          </div>
        </React.Fragment>
      }
    </React.Fragment>
  );
};

AllGames.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = ({
  router: {
    location,
  },
}) => ({ location });

export default connect(mapStateToProps)(injectIntl(AllGames));
